/**
 * The what-if, plotted — the mock's `chart3`, transcribed.
 *
 * ── It is the mock's own SVG, not an interpretation of it ───────────────────────────────────
 * Owner instruction, 15 September 2026: this plot must be IDENTICAL to
 * `Builder planu - modal - progresja`'s, in behaviour and in pixels. Every number below is read off
 * that file's `chart3` builder — the `0 0 560 146` box, the 50 / 50 / 60 / 30 pads, the node at `r 5`
 * and the verdict badge at `r 6` thirteen units to its right, the four label baselines measured up
 * from `ys3(val) − 14`, and the `popIn` / `markIn` stagger. What was here before was the same drawing
 * at a taller box with a deeper top pad and a cap on how many points could be labelled; all three are
 * withdrawn, along with the label-height logic that went with them.
 *
 * ONE owner-directed departure, 15 September 2026: a labelled VALUE AXIS on the left, scaled to the
 * plotted values a tick beyond each end, with its unit over the ticks and a faint rule on each. The
 * mock fits the scale to the run's range with no axis at all, so no step had a size to be read off.
 *
 * A SECOND, 12 October 2026, when the sheet took the whole day modal: the box is the mock's 560
 * units WIDE AT LEAST, and wider where the plot is laid out wider — one unit to one CSS pixel. The
 * mock's fixed box only scaled up, so a plot twice as wide printed every label twice as large and
 * still stacked eight cycles' labels 61 units apart, which is where they collided. Widening the box
 * instead spreads the cycles and keeps every size, pad and baseline above as it is. Below 560 — the
 * day's narrow dock — the box stays 560 and scales down, exactly as before.
 *
 * ── It draws `model/trajectory.ts`'s answer and computes nothing ────────────────────────────
 * Every number came out of `trajectory()`, which projects through `advanceFieldValue` — the same
 * function the matrix projects through and the same arithmetic the backend's parity fixture pins.
 * That is the one thing the mock is NOT followed on: it rounds to two decimals and describes the
 * in-session plate policy, which would put a different number under the same rule two boxes apart.
 *
 * ── A local SVG, not a chart library ────────────────────────────────────────────────────────
 * Five points on one line with labels over them. `shared/ui/chart` is the vendored bklit surface for
 * the analytics screens; nothing in it draws this, and pulling it in for five points would mean
 * Tailwind scaffolding inside a builder surface.
 *
 * ── The line is not the only channel ────────────────────────────────────────────────────────
 * `role="img"` swallows its subtree, so the accessible NAME has to carry the whole shape — it spells
 * the run out as a list of cycles and values, and the scenario's verdict per cycle with it.
 */

import { useRef, type CSSProperties } from 'react';

import { useLaidOutWidth } from '~/shared/lib/useLaidOutWidth';

import type {
  TrajectoryLabel,
  TrajectoryPoint,
  TrajectoryValueAxis,
} from '../model/trajectory';

import styles from './TrajectoryPreview.module.css';

/** The plot box, in SVG user space — `W3`, `H3`, `pl3`, `pr3`, `pt3`, `pb3`. `W3` is its least width. */
const MIN_WIDTH = 560;
const HEIGHT = 146;
/**
 * The mock's 50 plus the value axis the owner added on 15 September 2026: its tick numbers stand
 * right-aligned at `AXIS_TEXT_X`, clear of the first node's label stack.
 */
const PAD_LEFT = 80;
const PAD_RIGHT = 50;
const PAD_TOP = 60;
const PAD_BOTTOM = 30;

/** Where the value axis's tick numbers and its unit END, and where its grid lines start. */
const AXIS_TEXT_X = 30;
const AXIS_LINE_X = 36;

/** The verdict badge sits 13 units right of its node. */
const BADGE_OFFSET = 13;

/**
 * The four stacked lines over a node, as baselines measured UP from it.
 *
 * The mock writes them against `baseY = ys3(val) − 14` and then at −11, −25 and −36 from there. Read
 * bottom-up: what the client did, „ZROBIŁ", what the plan asked, „ZADANE". A point with no delivery
 * to state carries the bottom line alone, which is how the mock draws its base cycle.
 */
const LABEL_BASELINES = {
  doneValue: 14,
  doneCaption: 25,
  prescribedValue: 39,
  prescribedCaption: 50,
} as const;

export interface TrajectoryChartProps {
  readonly points: readonly TrajectoryPoint[];
  /** `'static'` draws a solid line, `'fromResult'` a dashed one — it is a prediction. */
  readonly family: 'static' | 'fromResult' | 'none';
  /** Each point's lines, worded by `trajectoryNarration`. Parallel to `points`. */
  readonly labels: readonly TrajectoryLabel[];
  /** The eyebrow over the prescribed line — „zadane". The done line's own is part of its label. */
  readonly captionPrescribed: string;
  /**
   * The value axis, `trajectoryValueAxis`'s answer: each node's height, from the first tick to the
   * last. It replaced the mock's unlabelled range-fitted scale (owner, 15 September 2026).
   */
  readonly valueAxis: TrajectoryValueAxis;
  /** The axis's unit, over its ticks — „kg", „powt.", „s". */
  readonly axisTitle: string;
  /** A tick's number in the reader's locale, „2,5". */
  readonly formatTick: (tick: number) => string;
  /** The cycle labels the matrix prints, so the two grids read the same. */
  readonly cycleLabels: readonly string[];
  /**
   * Which column the client is standing in, as a position — the mock's `NOW - 1`. It carries the
   * dashed guide line and whitens that cycle's axis label, exactly as `chart3` draws them. `null` on
   * a template, where there is no client to mark.
   */
  readonly nowIndex: number | null;
  readonly ariaLabel: string;
  readonly reducedMotion: boolean;
}

export function TrajectoryChart({
  points,
  family,
  labels,
  captionPrescribed,
  valueAxis,
  axisTitle,
  formatTick,
  cycleLabels,
  nowIndex,
  ariaLabel,
  reducedMotion,
}: TrajectoryChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const laidOutWidth = useLaidOutWidth(svgRef, points.length > 0);
  if (points.length === 0) return null;
  const width = Math.max(MIN_WIDTH, laidOutWidth ?? MIN_WIDTH);

  const plotted = points.map((point, index) => ({
    index,
    value: valueAxis.values[index] ?? 0,
    isSimulated: point.isSimulated,
  }));
  /* The SOLID run is every cycle the engine is not answering for: the lived sessions and a past cycle
   * the record holds nothing for. The simulated run picks up at the last of them rather than beside
   * it, one continuous line, two statements. With nothing behind the client it is the whole run,
   * which is the template variant unchanged. */
  const realPoints = plotted.filter((entry) => !entry.isSimulated);
  const simulated = [...realPoints.slice(-1), ...plotted.filter((entry) => entry.isSimulated)];

  function x(index: number): number {
    if (points.length === 1) return PAD_LEFT;
    return PAD_LEFT + (index / (points.length - 1)) * (width - PAD_LEFT - PAD_RIGHT);
  }
  /** The first tick at the bottom of the plot and the last at its top, both labelled. */
  function y(value: number): number {
    const range = valueAxis.max - valueAxis.min || 1;
    return PAD_TOP + (1 - (value - valueAxis.min) / range) * (HEIGHT - PAD_TOP - PAD_BOTTOM);
  }

  /** The `points` attribute of one run — the mock writes the same `x,y` pairs joined by spaces. */
  function polyline(run: readonly { readonly index: number; readonly value: number }[]): string {
    return run.map((entry) => `${String(x(entry.index))},${String(y(entry.value))}`).join(' ');
  }

  return (
    <svg
      ref={svgRef}
      className={styles.svg}
      viewBox={`0 0 ${String(width)} ${String(HEIGHT)}`}
      role="img"
      aria-label={ariaLabel}
    >
      {/* The value axis: its unit over the ticks, a number and a faint rule on every tick. */}
      <text className={styles.axisLabel} x={AXIS_TEXT_X} y={PAD_TOP - 12} textAnchor="end">
        {axisTitle}
      </text>
      {valueAxis.ticks.map((tick) => (
        <g key={`tick-${String(tick)}`}>
          <line
            className={styles.gridLine}
            x1={AXIS_LINE_X}
            x2={width - PAD_RIGHT / 2}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text className={styles.axisLabel} x={AXIS_TEXT_X} y={y(tick) + 3} textAnchor="end">
            {formatTick(tick)}
          </text>
        </g>
      ))}

      {points.map((point, index) => (
        <line
          key={`grid-${String(point.cycle)}`}
          className={styles.gridLine}
          /* The mock's own brighter, dashed guide over the cycle the client is standing in. */
          data-now={index === nowIndex ? 'true' : undefined}
          x1={x(index)}
          x2={x(index)}
          y1={PAD_TOP - 6}
          y2={HEIGHT - PAD_BOTTOM + 3}
        />
      ))}

      {/* TWO LINES, NOT ONE, and that is the owner's „real told apart from simulated". The lived run
        * is solid in neutral ink — it happened, and no rule family has an opinion about it — and the
        * scenario's run carries the family's own colour and dash. The simulated segment STARTS at the
        * last lived point so the run reads as one continuous line, and the scenario segment still
        * cannot touch a single millimetre of the real part. */}
      {realPoints.length > 1 && (
        <polyline className={styles.line} data-real="true" points={polyline(realPoints)} />
      )}
      <polyline className={styles.line} data-family={family} points={polyline(simulated)} />

      {points.map((point, index) => {
        const label = labels[index];
        const cx = x(index);
        const cy = y(valueAxis.values[index] ?? 0);
        return (
          <g
            key={`point-${String(point.cycle)}`}
            className={styles.point}
            /* The mock's `popIn` stagger, 0.28s apart, and `markIn` a fifth of a second behind each
              * node. Held at the finished frame under reduced motion rather than merely undelayed — a
              * `both`-filled entrance with its delay intact and no duration leaves every point
              * invisible. */
            style={
              reducedMotion ? undefined : ({ '--point-index': String(index) } as CSSProperties)
            }
          >
            {/* The cycle's VERDICT, the mock's own filled mark beside the node: it carries a glyph
              * as well as a fill, because a green disc and a pink disc are the same disc to anyone
              * who cannot tell them apart, and the run is spelled out in the chart's name besides. */}
            {point.delivered !== null && (
              <g className={styles.mark}>
                <circle
                  cx={cx + BADGE_OFFSET}
                  cy={cy}
                  r={6}
                  className={styles.verdict}
                  data-delivered={point.delivered ? 'true' : 'false'}
                />
                <text
                  className={styles.verdictGlyph}
                  x={cx + BADGE_OFFSET}
                  y={cy + 3}
                  textAnchor="middle"
                >
                  {point.delivered ? '✓' : '✗'}
                </text>
              </g>
            )}
            <circle
              cx={cx}
              cy={cy}
              r={5}
              className={styles.node}
              data-family={family}
              /* A lived session is drawn in neutral ink like the line under it, so the family's colour
                * stays what it has always been on this plot: a statement about the PREDICTION. */
              data-real={point.isSimulated ? undefined : 'true'}
              data-delivered={point.delivered === false ? 'false' : undefined}
            />
            {/* The mock's label stack: one bare line on a cycle nobody was asked to deliver, and the
              * four-line „ZADANE / ZROBIŁ" stack where there is a delivery to state. */}
            {label?.done == null ? (
              <text
                className={styles.label}
                x={cx}
                y={cy - LABEL_BASELINES.doneValue}
                textAnchor="middle"
              >
                {label?.prescribed ?? ''}
              </text>
            ) : (
              <>
                <text
                  className={styles.caption}
                  x={cx}
                  y={cy - LABEL_BASELINES.prescribedCaption}
                  textAnchor="middle"
                >
                  {captionPrescribed}
                </text>
                <text
                  className={styles.label}
                  x={cx}
                  y={cy - LABEL_BASELINES.prescribedValue}
                  textAnchor="middle"
                >
                  {label.prescribed}
                </text>
                <text
                  className={styles.caption}
                  data-tone={label.done.tone}
                  x={cx}
                  y={cy - LABEL_BASELINES.doneCaption}
                  textAnchor="middle"
                >
                  {label.done.caption}
                  {/* The shortfall's mark keeps its OWN case: the mock writes „ZROBIŁ ↓powt.", and a
                    * `text-transform` over the whole line reads „↓POWT." — the abbreviation's full
                    * stop with a shouted stem in front of it. */}
                  {label.done.mark !== null && (
                    <tspan className={styles.captionMark}>{` ${label.done.mark}`}</tspan>
                  )}
                </text>
                {/* EMPTY on a cycle nobody trained: the caption above already carries „bez treningu",
                  * and there is no performance to state under it. */}
                {label.done.text !== '' && (
                  <text
                    className={styles.doneLabel}
                    data-tone={label.done.tone}
                    x={cx}
                    y={cy - LABEL_BASELINES.doneValue}
                    textAnchor="middle"
                  >
                    {label.done.text}
                  </text>
                )}
              </>
            )}
          </g>
        );
      })}

      {/* One label per cycle, at the mock's own baseline. */}
      {points.map((point, index) => (
        <text
          key={`axis-${String(point.cycle)}`}
          className={styles.axisLabel}
          data-now={index === nowIndex ? 'true' : undefined}
          x={x(index)}
          y={HEIGHT - 7}
          textAnchor="middle"
        >
          {cycleLabels[index] ?? ''}
        </text>
      ))}
    </svg>
  );
}
